import React, { useState, useEffect } from 'react';
import { FaBook, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSearch } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as masterService from '../services/masterService';

const CreateSubject = () => {
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    name: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch subjects on mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setIsLoading(true);
      const response = await masterService.getAllSubjects();
      // Assuming response.data contains the list of subjects
      if (response && response.success && Array.isArray(response.data)) {
        setSubjects(response.data);
      } else if (Array.isArray(response)) {
        // Fallback if API returns array directly
        setSubjects(response);
      } else {
        // console.error("Unexpected response format:", response);
        // showToast('Failed to load subjects: Invalid format', 'error');
        setSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      showToast(error.message || 'Failed to fetch subjects', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      setIsLoading(true);
      if (editingId) {
        await masterService.updateSubject(editingId, formData);
        showToast('Subject updated successfully', 'success');
        setEditingId(null);
      } else {
        await masterService.createSubject(formData);
        showToast('Subject created successfully', 'success');
      }

      setFormData({ name: '' });
      // Refresh list
      await fetchSubjects();

    } catch (error) {
      console.error('Error saving subject:', error);
      showToast(error.message || 'Failed to save subject', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (subject) => {
    setFormData({
      name: subject.name
    });
    setEditingId(subject.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        setIsLoading(true);
        await masterService.deleteSubject(id);
        showToast('Subject deleted successfully', 'success');
        await fetchSubjects();
      } catch (error) {
        console.error('Error deleting subject:', error);
        showToast(error.message || 'Failed to delete subject', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({
      name: ''
    });
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-8 px-4">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="max-w mx-auto">
        {/* Header */}
        <div className="rounded-2xl shadow-xl p-6 mb-8" style={darkGradient}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <FaBook className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Department Management</h1>
              <p className="text-blue-100 mt-1">Create and manage academic departments</p>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FaPlus className="text-blue-500" />
            {editingId ? 'Edit Department' : 'Add New Department'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department Name <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Computer Science"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                    disabled={isLoading}
                  />
                  <div className="flex gap-2 items-end">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap h-[52px] disabled:opacity-50"
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <FaSave />
                      )}
                      {editingId ? 'Update' : 'Save'}
                    </button>

                    {editingId && (
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all whitespace-nowrap h-[52px] disabled:opacity-50"
                      >
                        <FaTimes />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
            <h2 className="text-2xl font-bold text-gray-900">
              All Departments ({subjects.length})
            </h2>

            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search departments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all w-full md:w-80"
              />
            </div>
          </div>

          {isLoading && subjects.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-500 text-lg">Loading departments...</p>
            </div>
          ) : filteredSubjects.length === 0 ? (
            <div className="text-center py-12">
              <FaBook className="text-6xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm ? 'No departments found matching your search' : 'No departments created yet'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={darkGradient}>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Name</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredSubjects.map((subject, index) => (
                    <tr key={subject.id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">{subject.name}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(subject)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(subject.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateSubject;